import { API } from "@/api"
import { DeviceInfoRes, HttpAPIError } from "@/api/model"
import {
  BizError,
  BizResult,
  DeviceStatus,
  Ok,
  RemoteDeskInfo,
} from "@/models/biz_model"
import {
  ERR_AGENT_OFFLINE,
  ERR_DEV_LOCK,
  ERR_DEV_POWER_STOP_RESUME,
  ERR_DEV_STATUS_EXCEPTION,
  ERR_DEV_STATUS_NOT_FOUND,
} from "@/models/constants"
import { DataReportUsecase } from "./data_report"
import { LocalDevIdCache } from "@/utils/storage"

let curLocalDevId = ""

async function powerOffOrRestart(
  stopResumeType: string,
  onProgress?: (msg: string) => void
): Promise<BizResult<number>> {
  // 登录密码校验通过，调用/device/getDeviceList接口，获取设备列表
  const deviceRes = await API.getDeviceList()
  if (deviceRes instanceof HttpAPIError) {
    // 上报获取机器列表失败
    DataReportUsecase.reportError(deviceRes.code, deviceRes.msg ?? "")
    // 获取设备列表失败
    console.log("DeviceUsecase stop resume get device list error: ", deviceRes)
    return BizError.fromHttpError(deviceRes)
  }

  // 检查设备列表是否为空
  if (!deviceRes || deviceRes.length === 0) {
    console.error("DeviceUsecase stop resume: device list is empty")
    DataReportUsecase.reportError(ERR_DEV_STATUS_NOT_FOUND, "设备列表为空")
    return new BizError(ERR_DEV_STATUS_NOT_FOUND)
  }

  const { desktopID, deviceID } = deviceRes[0]

  if (desktopID.startsWith("b")) {
    return BizError.fromHttpError(new HttpAPIError(ERR_DEV_POWER_STOP_RESUME))
  }

  const devStatusRes = await _getDeviceStatus(desktopID, deviceID)
  if (devStatusRes instanceof BizError) {
    return devStatusRes
  }

  const devStatus = devStatusRes.unwrap() as
    | DeviceStatus
    | "Rebooting"
    | "ResumeStop"
  const normalizedDevStatus: DeviceStatus =
    devStatus === "Rebooting"
      ? "Restarting"
      : devStatus === "ResumeStop"
        ? "Stopped"
        : devStatus

  if (stopResumeType === "resume") {
    if (
      devStatus === "Starting" ||
      devStatus === "Restarting" ||
      devStatus === "Rebooting"
    ) {
      onProgress?.("云电脑正在重启，请稍候...")
    }
    return await _restartDevice(
      desktopID,
      deviceID,
      normalizedDevStatus,
      onProgress
    )
  }

  if (stopResumeType === "stop") {
    if (devStatus === "Stopping") {
      onProgress?.("云电脑正在关机，请稍候...")
    }

    if (devStatus === "Stopped" || devStatus === "ResumeStop") {
      onProgress?.("云电脑已处于关机状态")
    }

    return await _stopDevice(
      desktopID,
      deviceID,
      normalizedDevStatus,
      onProgress
    )
  }

  return new BizError(ERR_DEV_STATUS_EXCEPTION)
}

/**
 * 获取当前用户分配的云桌面设备列表
 */
async function getDevices(): Promise<BizResult<RemoteDeskInfo[]>> {
  const res = await API.getDeviceList()
  if (res instanceof HttpAPIError) {
    // 上报获取机器列表失败
    DataReportUsecase.reportError(res.code, res.msg ?? "")
    // 获取企业账号设备列表失败
    console.log("DeviceUsecase get kc device list error: ", res)
    return BizError.fromHttpError(res)
  }

  const rawDevList: DeviceInfoRes[] = res
  const devices: RemoteDeskInfo[] = []
  for (const dev of rawDevList) {
    if (!dev) {
      DataReportUsecase.reportError(
        ERR_DEV_STATUS_NOT_FOUND,
        `获取设备状态失败-未返回设备状态`
      )
      console.error(`DeviceUseCase cannot find device status and deviceList`)
      return new BizError(ERR_DEV_STATUS_NOT_FOUND)
    }

    if (dev.businessStatus === "Lock") {
      DataReportUsecase.reportError(ERR_DEV_LOCK, `机器欠费 已被锁定`)
      return new BizError(ERR_DEV_LOCK)
    }

    if (dev.status === "Pending") {
      DataReportUsecase.reportError(
        ERR_DEV_STATUS_EXCEPTION,
        `获取设备状态失败-机器异常`,
        {
          desktopId: dev.desktopID,
          deviceId: String(dev.deviceID),
        }
      )
      // 机器状态异常
      return new BizError(ERR_DEV_STATUS_EXCEPTION)
    }

    if (dev.status === "Running" && !dev.online) {
      // 上报远端设备不在线
      DataReportUsecase.reportLog("signal_agent_online_err", {
        desktopId: dev.desktopID,
        deviceId: String(dev.deviceID),
      })
      console.error(`DeviceUseCase desktop ${dev.desktopID} agent offline`)
      return new BizError(ERR_AGENT_OFFLINE)
    }

    // 修正 gatewayIp, privateServerIp, status 字段
    const deviceInfo = {
      id: dev.deviceID,
      desktopId: dev.desktopID,
      name: dev.deviceName,
      serverPrivateIp: dev.serverPrivateIp,
      gateWayIp: dev.gateWayIP,
      desktopPublicIp: dev.desktopPublicIp,
      idcId: dev.node,
      status: dev.status as DeviceStatus,
      online: dev.online,
      businessStatus: dev.businessStatus,
    }
    devices.push(deviceInfo)
  }

  return new Ok(devices)
}

/**
 *  确保云桌面设备已开机
 */
async function ensureDeviceStart(
  device: RemoteDeskInfo
): Promise<BizResult<number>> {
  if (device.status === "Running") {
    return new Ok(0)
  }

  let devStatus = device.status
  if (devStatus === "Pending" || devStatus === "Stopping") {
    const res = await _checkDeviceStatusUntil(
      "Stopped",
      device.desktopId,
      device.id
    )
    if (res instanceof BizError) {
      return res
    }
    devStatus = "Stopped"
  }

  if (devStatus === "Stopped") {
    const res = await API.deviceControl("start", device.desktopId)
    if (res instanceof HttpAPIError) {
      console.error("DeviceUseCase deviceControl error: ", res)
      DataReportUsecase.reportError(res.code, res.msg, {
        desktopId: device.desktopId,
        deviceId: String(device.id),
      })
      return BizError.fromHttpError(res)
    }
    devStatus = "Starting"
  }

  if (devStatus !== "Starting" && devStatus !== "Restarting") {
    return new BizError(ERR_DEV_STATUS_EXCEPTION)
  }

  // 等待设备开机
  const res = await _checkDeviceStatusUntil(
    "Running",
    device.desktopId,
    device.id,
    10000
  )
  return res
}

/**
 *  确保agent在线
 */
async function ensureAgentOnline(
  device: RemoteDeskInfo
): Promise<BizResult<number>> {
  let agentStatus
  if (device.online) {
    agentStatus = device.online
  } else {
    agentStatus = await API.getAgentStatus(device.id)
  }

  if (agentStatus === "online") {
    return new Ok(0)
  }

  // agent状态查询中
  const res = await _checkAgentStatusUntil(device.desktopId, device.id, 5000)
  return res
}

function _checkAgentStatusUntil(
  desktopId: string,
  deviceId: number,
  duration: number = 5000
): Promise<BizResult<number>> {
  return new Promise<BizResult<number>>((resolve) => {
    let timer: number | null = null
    let settled = false

    const finish = (result: BizResult<number>) => {
      if (settled) {
        return
      }
      settled = true
      if (timer !== null) {
        window.clearInterval(timer)
      }
      resolve(result)
    }

    const pollTask = async () => {
      const res = await API.getAgentStatus(deviceId)

      if (res instanceof HttpAPIError) {
        // 获取agent状态失败
        console.error("Deviceusecase get agent status error: ", res)

        DataReportUsecase.reportError(res.code, res.msg, {
          desktopId,
          deviceId: String(deviceId),
        })

        finish(BizError.fromHttpError(res))
        return
      }

      if (res === "online") {
        finish(new Ok(0))
      }
    }

    // 开启轮训
    timer = window.setInterval(() => {
      pollTask()
    }, duration)
  })
}

async function _getDeviceStatus(
  desktopId: string,
  deviceId: number
): Promise<BizResult<DeviceStatus>> {
  const res = await API.getDeviceStatus([desktopId])

  if (res instanceof HttpAPIError) {
    console.error("Deviceusecase get device status error: ", res)
    DataReportUsecase.reportError(res.code, res.msg, {
      desktopId,
      deviceId: String(deviceId),
    })
    return BizError.fromHttpError(res)
  }

  const status = res[0]?.status as DeviceStatus | undefined
  if (!status) {
    DataReportUsecase.reportError(
      ERR_DEV_STATUS_NOT_FOUND,
      "获取设备状态失败-未返回设备状态",
      {
        desktopId,
        deviceId: String(deviceId),
      }
    )
    return new BizError(ERR_DEV_STATUS_NOT_FOUND)
  }

  return new Ok(status)
}

async function _controlDevice(
  action: "start" | "stop" | "resume",
  desktopId: string,
  deviceId: number,
  onProgress?: (msg: string) => void
): Promise<BizResult<number>> {
  const res = await API.deviceControl(action, desktopId)

  if (res instanceof HttpAPIError) {
    console.error("DeviceUseCase stop resume deviceControl error: ", res)
    DataReportUsecase.reportError(res.code, res.msg, {
      desktopId,
      deviceId: String(deviceId),
    })
    return BizError.fromHttpError(res)
  }

  if (res?.status !== undefined && res.status !== "success") {
    DataReportUsecase.reportError(
      ERR_DEV_STATUS_EXCEPTION,
      "云电脑控制操作返回状态异常",
      {
        desktopId,
        deviceId: String(deviceId),
      }
    )
    return new BizError(ERR_DEV_STATUS_EXCEPTION)
  }

  if (action === "resume") {
    onProgress?.("开始 重启 云电脑")
  } else if (action === "stop") {
    onProgress?.("开始 关机 云电脑")
  }

  return new Ok(0)
}

async function _restartDevice(
  desktopId: string,
  deviceId: number,
  status: DeviceStatus,
  onProgress?: (msg: string) => void
): Promise<BizResult<number>> {
  if (status === "Running") {
    const controlRes = await _controlDevice(
      "resume",
      desktopId,
      deviceId,
      onProgress
    )
    if (controlRes instanceof BizError) {
      return controlRes
    }
    status = "Restarting"
    onProgress?.("云电脑正在重启，请稍候...")
  }

  if (status === "Stopping") {
    onProgress?.("云电脑正在重启，请稍候...")
    const stoppedRes = await _checkDeviceStatusUntil(
      "Stopped",
      desktopId,
      deviceId
    )
    if (stoppedRes instanceof BizError) {
      return stoppedRes
    }
    status = "Stopped"
  }

  if (status === "Stopped") {
    onProgress?.("云电脑正在重启，请稍候...")
    const controlRes = await _controlDevice("start", desktopId, deviceId)
    if (controlRes instanceof BizError) {
      return controlRes
    }
    status = "Starting"
  }

  if (status !== "Starting" && status !== "Restarting") {
    DataReportUsecase.reportError(ERR_DEV_STATUS_EXCEPTION, "机器状态异常", {
      desktopId,
      deviceId: String(deviceId),
    })
    return new BizError(ERR_DEV_STATUS_EXCEPTION)
  }

  onProgress?.("云电脑正在重启，请稍候...")
  const runningRes = await _checkDeviceStatusUntil(
    "Running",
    desktopId,
    deviceId
  )
  if (runningRes instanceof BizError) {
    return runningRes
  }

  const agentRes = await _checkAgentStatusUntil(desktopId, deviceId, 5000)
  if (agentRes instanceof BizError) {
    return agentRes
  }

  onProgress?.("重启成功")
  return new Ok(0)
}

async function _stopDevice(
  desktopId: string,
  deviceId: number,
  status: DeviceStatus,
  onProgress?: (msg: string) => void
): Promise<BizResult<number>> {
  if (status === "Starting" || status === "Restarting") {
    onProgress?.("等待云电脑启动完成后 关机...")
    const runningRes = await _checkDeviceStatusUntil(
      "Running",
      desktopId,
      deviceId
    )
    if (runningRes instanceof BizError) {
      return runningRes
    }
    status = "Running"
  }

  if (status === "Running") {
    const controlRes = await _controlDevice(
      "stop",
      desktopId,
      deviceId,
      onProgress
    )
    if (controlRes instanceof BizError) {
      return controlRes
    }
    status = "Stopping"
    onProgress?.("云电脑正在关机，请稍候...")
  }

  if (status === "Stopping") {
    onProgress?.("云电脑正在关机，请稍候...")
    return await _checkDeviceStatusUntil("Stopped", desktopId, deviceId)
  }

  if (status === "Stopped") {
    onProgress?.("关机成功")
    return new Ok(0)
  }

  DataReportUsecase.reportError(ERR_DEV_STATUS_EXCEPTION, "机器状态异常", {
    desktopId,
    deviceId: String(deviceId),
  })
  return new BizError(ERR_DEV_STATUS_EXCEPTION)
}

/**
 *  轮训检查设备状态，直到达到期望状态
 * @param status 期望的设备状态
 * @param desktopId 设备 Desktop Id
 * @param duration 轮训时长，单位ms
 */
function _checkDeviceStatusUntil(
  status: DeviceStatus,
  desktopId: string,
  deviceId: number,
  duration: number = 10000
): Promise<BizResult<number>> {
  return new Promise<BizResult<number>>((resolve) => {
    let timer: number | null = null

    const pollTask = async () => {
      const res = await API.getDeviceStatus([desktopId])

      if (res instanceof HttpAPIError) {
        if (timer !== null) {
          window.clearInterval(timer)
        }
        // 获取设备状态失败
        console.error("Deviceusecase get device status error: ", res)
        DataReportUsecase.reportError(res.code, res.msg, {
          desktopId,
          deviceId: String(deviceId),
        })
        resolve(BizError.fromHttpError(res))
        return
      }

      if (res[0].status === status) {
        if (timer !== null) {
          window.clearInterval(timer)
        }
        resolve(new Ok(0))
      }
    }

    // 开启轮训
    timer = window.setInterval(() => {
      pollTask()
    }, duration)
  })
}

// 回收本地设备ID
async function recycleLocalDeviceId() {
  if (curLocalDevId) {
    LocalDevIdCache.returnLocalDevIdToPool(curLocalDevId)
  }
}

// 获取本地设备ID
async function getLocalDeviceId(): Promise<BizResult<string>> {
  if (curLocalDevId) {
    return new Ok(curLocalDevId)
  }
  const pool = LocalDevIdCache.getLocalDevIdPool()
  if (pool.size === 0) {
    // 如果池为空，并且没有本地设备ID，则获取新设备id
    const res = await API.getDeviceID()
    if (res instanceof HttpAPIError) {
      DataReportUsecase.reportError(res.code, res.msg ?? "")
      return BizError.fromHttpError(res)
    }
    // 确保将数字转换为字符串
    const deviceIdStr = String(res)
    curLocalDevId = deviceIdStr
    return new Ok(deviceIdStr)
  }

  const deviceId = LocalDevIdCache.getRandomLocalDevIdFromPool(pool)
  curLocalDevId = deviceId
  return new Ok(deviceId)
}

async function getDevice(
  deviceId: number,
  desktopId: string
): Promise<BizResult<RemoteDeskInfo>> {
  const devStatusRes = await API.getDeviceStatus([desktopId])
  if (devStatusRes instanceof HttpAPIError) {
    DataReportUsecase.reportError(devStatusRes.code, devStatusRes.msg)
    console.error("DeviceUsecase get device status error: ", devStatusRes)
    return new BizError(devStatusRes.code)
  }
  const { gateWayIP, serverPrivateIp, status, node, desktopPublicIp } =
    devStatusRes[0]
  const device: RemoteDeskInfo = {
    id: deviceId,
    desktopId,
    name: "",
    gateWayIp: gateWayIP,
    desktopPublicIp,
    idcId: node,
    serverPrivateIp,
    status: status as DeviceStatus,
  }
  return new Ok(device)
}

export const DeviceUseCase = {
  getDevices,
  ensureDeviceStart,
  ensureAgentOnline,
  getLocalDeviceId,
  recycleLocalDeviceId,
  getDevice,
  powerOffOrRestart,
}
